import { useCallback, useRef, useState } from "react";
import { PDFDocument } from "pdf-lib";
import { useDropzone } from "react-dropzone";
import {
  FileSignature,
  Upload,
  Download,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  FileText,
  CheckCircle2,
  MousePointerClick,
  ShieldCheck,
  Lock,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PdfViewer } from "@/components/PdfViewer";
import { SignaturePanel } from "@/components/SignaturePanel";

const MIN_W = 420;
const MAX_W = 1100;

export default function SignDocument() {
  const [file, setFile] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [renderWidth, setRenderWidth] = useState(680);
  const [signature, setSignature] = useState(null); // active { dataUrl, width, height }
  const [placements, setPlacements] = useState([]); // [{ id, page, relX, relY, relW, dataUrl, imgW, imgH }]
  const [downloading, setDownloading] = useState(false);

  const onDrop = useCallback((accepted) => {
    const f = accepted[0];
    if (!f) return;
    if (f.type !== "application/pdf") {
      toast.error("Veuillez sélectionner un fichier PDF.");
      return;
    }
    setFile(f);
    setPageNumber(1);
    setPlacements([]);
    toast.success(`« ${f.name} » chargé.`);
  }, []);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: { "application/pdf": [".pdf"] },
    multiple: false,
    noClick: true,
    noKeyboard: true,
  });

  const resetDoc = () => {
    setFile(null);
    setNumPages(0);
    setPageNumber(1);
    setSignature(null);
    setPlacements([]);
  };

  const uid = () =>
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const addPlacement = (p) => {
    setPlacements((prev) => [...prev, { id: uid(), ...p }]);
    toast.success("Signature ajoutée sur le document.");
  };
  const updatePlacement = (id, patch) =>
    setPlacements((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const removePlacement = (id) => setPlacements((prev) => prev.filter((p) => p.id !== id));
  const clearPlacements = () => setPlacements([]);

  const handleDownload = async () => {
    if (!file || placements.length === 0) return;
    setDownloading(true);
    try {
      const bytes = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(bytes);
      const cache = new Map();
      for (const pl of placements) {
        const page = pdfDoc.getPage(pl.page - 1);
        const { width: pw, height: ph } = page.getSize();
        let png = cache.get(pl.dataUrl);
        if (!png) {
          png = await pdfDoc.embedPng(pl.dataUrl);
          cache.set(pl.dataUrl, png);
        }
        const tw = pl.relW * pw;
        const th = tw * (pl.imgH / pl.imgW);
        const x = pl.relX * pw;
        const y = ph - pl.relY * ph - th;
        page.drawImage(png, { x, y, width: tw, height: th });
      }
      const out = await pdfDoc.save();
      const blob = new Blob([out], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name.replace(/\.pdf$/i, "") + "-signe.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Document signé téléchargé.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la génération du PDF signé.");
    } finally {
      setDownloading(false);
    }
  };

  const steps = [
    { label: "Document", done: !!file },
    { label: "Signature", done: !!signature },
    { label: "Position", done: placements.length > 0 },
    { label: "Télécharger", done: false },
  ];

  return (
    <div className="flex h-screen flex-col bg-background">
      {/* Header */}
      <header className="flex shrink-0 items-center justify-between border-b border-border px-6 py-3.5">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <FileSignature className="h-5 w-5" />
          </div>
          <div className="leading-none">
            <h1 className="font-display text-lg font-bold tracking-tight">ParaphePDF</h1>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              Signature de documents · pad Topaz
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div
            className="hidden items-center gap-1.5 rounded-full border border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/10 px-3 py-1.5 sm:flex"
            data-testid="security-badge"
            title="Vos documents et signatures ne quittent jamais ce poste"
          >
            <ShieldCheck className="h-4 w-4 text-[hsl(var(--success))]" />
            <span className="text-xs font-semibold text-[hsl(var(--success))]">
              Traitement 100 % local
            </span>
          </div>
          {file && (
            <Button variant="outline" size="sm" onClick={resetDoc} data-testid="new-document-btn">
              <RotateCcw className="mr-2 h-4 w-4" /> Nouveau document
            </Button>
          )}
        </div>
      </header>

      {/* Empty state */}
      {!file ? (
        <div className="flex flex-1 items-center justify-center p-6 workspace-grid">
          <div
            {...getRootProps()}
            className={`w-full max-w-xl animate-fade-up rounded-xl border-2 border-dashed bg-white/80 p-12 text-center backdrop-blur transition-colors ${
              isDragActive ? "border-primary bg-primary/5" : "border-border"
            }`}
            data-testid="dropzone"
          >
            <input {...getInputProps()} data-testid="file-input" />
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-secondary">
              <Upload className="h-6 w-6 text-primary" />
            </div>
            <span className="eyebrow">Étape 1</span>
            <h2 className="mt-2 font-display text-3xl font-bold tracking-tight">
              Ouvrez un fichier PDF
            </h2>
            <p className="mx-auto mt-3 max-w-md text-base leading-relaxed text-muted-foreground">
              Glissez-déposez votre document ici, ou parcourez vos fichiers. La signature reste
              sur votre poste — rien n'est envoyé sur internet.
            </p>
            <Button className="mt-6" size="lg" onClick={open} data-testid="upload-pdf-button">
              <FileText className="mr-2 h-4 w-4" /> Parcourir les fichiers
            </Button>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-4 py-2">
              <Lock className="h-4 w-4 text-[hsl(var(--success))]" />
              <span className="text-xs font-medium text-muted-foreground">
                Connexion chiffrée · aucune donnée envoyée sur internet
              </span>
            </div>
          </div>
        </div>
      ) : (
        // Workspace
        <div className="flex min-h-0 flex-1">
          {/* Canvas */}
          <div className="flex min-w-0 flex-1 flex-col">
            {/* Toolbar */}
            <div className="flex shrink-0 items-center justify-between gap-4 border-b border-border bg-white px-5 py-2.5">
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={pageNumber <= 1}
                  onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="min-w-[88px] text-center text-sm tabular-nums text-muted-foreground">
                  Page {pageNumber} / {numPages || "…"}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={pageNumber >= numPages}
                  onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}
                  data-testid="next-page-btn"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setRenderWidth((w) => Math.max(MIN_W, w - 80))}
                  data-testid="zoom-out-btn"
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setRenderWidth((w) => Math.min(MAX_W, w + 80))}
                  data-testid="zoom-in-btn"
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Scrollable PDF area */}
            <div className="min-h-0 flex-1 overflow-auto p-8 workspace-grid" data-testid="pdf-scroll-area">
              <PdfViewer
                file={file}
                pageNumber={pageNumber}
                onLoadSuccess={setNumPages}
                renderWidth={renderWidth}
                activeSignature={signature}
                placements={placements}
                onAdd={addPlacement}
                onUpdate={updatePlacement}
                onRemove={removePlacement}
              />
            </div>
          </div>

          {/* Control panel */}
          <aside className="flex w-[360px] shrink-0 flex-col overflow-y-auto border-l border-border bg-white">
            {/* Stepper */}
            <div className="flex items-center gap-1 border-b border-border px-5 py-4">
              {steps.map((s, i) => (
                <div key={s.label} className="flex flex-1 items-center gap-1">
                  <div className="flex flex-col items-center gap-1">
                    <div
                      className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ${
                        s.done
                          ? "bg-[hsl(var(--success))] text-white"
                          : "bg-secondary text-muted-foreground"
                      }`}
                    >
                      {s.done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                    </div>
                    <span className="text-[10px] font-medium text-muted-foreground">{s.label}</span>
                  </div>
                  {i < steps.length - 1 && <div className="mb-4 h-px flex-1 bg-border" />}
                </div>
              ))}
            </div>

            <div className="space-y-6 p-5 pb-28">
              <section>
                <span className="eyebrow">Étape 2 · Signature</span>
                <h3 className="mb-3 mt-1 font-display text-xl font-semibold">Capturer la signature</h3>
                <SignaturePanel onCaptured={setSignature} />
              </section>

              <section className="border-t border-border pt-5">
                <span className="eyebrow">Étape 3 · Placement</span>
                <h3 className="mb-2 mt-1 font-display text-xl font-semibold">Positionner</h3>
                {!signature ? (
                  <p className="text-sm text-muted-foreground">
                    Validez d'abord une signature ci-dessus.
                  </p>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-start gap-2 rounded-md bg-secondary p-3 text-sm">
                      <MousePointerClick className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span className="text-muted-foreground">
                        Cliquez sur le document pour <b className="text-foreground">déposer une signature</b>.
                        Chaque clic en ajoute une nouvelle — placez-en autant que nécessaire. Glissez
                        une signature pour l'ajuster, ou passez la souris dessus pour la retirer.
                        Vous pouvez aussi capturer une nouvelle signature ci-dessus et la déposer en dessous.
                      </span>
                    </div>
                    <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                      <span className="text-sm font-medium" data-testid="placement-count">
                        {placements.length} signature{placements.length > 1 ? "s" : ""} placée
                        {placements.length > 1 ? "s" : ""}
                      </span>
                      {placements.length > 0 && (
                        <button
                          type="button"
                          onClick={clearPlacements}
                          data-testid="clear-placements-btn"
                          className="text-xs font-semibold text-destructive hover:underline"
                        >
                          Tout effacer
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </section>

              <section className="border-t border-border pt-5">
                <span className="eyebrow">Étape 4 · Finaliser</span>
                <h3 className="mb-3 mt-1 font-display text-xl font-semibold">Télécharger</h3>
                <Button
                  className="w-full"
                  size="lg"
                  disabled={placements.length === 0 || downloading}
                  onClick={handleDownload}
                  data-testid="download-pdf-button"
                >
                  <Download className="mr-2 h-4 w-4" />
                  {downloading ? "Génération…" : "Télécharger le PDF signé"}
                </Button>
              </section>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
